import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { useAuth } from '../../shared/contexts/UserContext';
import {
  CONNECTOR_OAUTH_MESSAGE_TYPE,
  beginConnectorOAuth,
  disconnectConnector,
  getConnectorPublicErrorMessage,
  getMyConnectors,
  validateAuthorizationUrl,
  validateConnector,
} from '../../shared/services/connectorService';
import { consoleCardSx, statusChipSx } from '../admin/consoleChips';
import {
  CAPABILITY_STATUSES,
  CapabilityStatusChip,
  CapabilityUnavailablePanel,
  deriveConnectorCapability,
} from './capabilityStatus';

const CONNECTOR_ID_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;
const ERROR_CODE_PATTERN = /^[a-z][a-z0-9_]{0,79}$/;
const KNOWN_STATUSES = new Set([
  'available',
  'connected',
  'error',
  'not_connected',
  'unavailable',
]);
const KNOWN_AUTH_MODES = new Set(['none', 'oauth']);
const KNOWN_CREDENTIAL_SCOPES = new Set(['none', 'org', 'user']);
const KNOWN_ACTIONS = new Set(['connect', 'disconnect', 'reconnect', 'validate']);
const OAUTH_PARAM_NAMES = ['connector_oauth', 'status', 'code', 'section'];
const EMPTY_CONNECTORS = Object.freeze([]);

function boundedText(value, maxLength) {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized && normalized.length <= maxLength ? normalized : null;
}

function normalizedIso(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function normalizeConnector(rawConnector) {
  if (!rawConnector || typeof rawConnector !== 'object' || Array.isArray(rawConnector)) {
    return null;
  }
  const id = boundedText(rawConnector.id || rawConnector.connectorId, 64);
  const name = boundedText(rawConnector.name, 80);
  const description = boundedText(rawConnector.description, 500);
  const status = boundedText(rawConnector.status, 40)?.toLowerCase();
  const authMode = boundedText(rawConnector.authMode, 20)?.toLowerCase();
  const credentialScope = boundedText(rawConnector.credentialScope, 20)?.toLowerCase();
  const outputClassification = boundedText(rawConnector.outputClassification, 80);
  const rawActions = rawConnector.actions;
  if (
    !id
    || !CONNECTOR_ID_PATTERN.test(id)
    || !name
    || !description
    || !KNOWN_STATUSES.has(status)
    || !KNOWN_AUTH_MODES.has(authMode)
    || !KNOWN_CREDENTIAL_SCOPES.has(credentialScope)
    || !Array.isArray(rawActions)
    || rawActions.some((action) => !KNOWN_ACTIONS.has(action))
    || new Set(rawActions).size !== rawActions.length
    || (authMode === 'none' && credentialScope !== 'none')
    || (authMode === 'none' && rawActions.some((action) =>
      ['connect', 'disconnect', 'reconnect', 'validate'].includes(action)))
  ) {
    return null;
  }
  const errorCode = rawConnector.errorCode == null
    ? null
    : boundedText(rawConnector.errorCode, 80);
  if (errorCode && !ERROR_CODE_PATTERN.test(errorCode)) return null;
  return Object.freeze({
    id,
    name,
    description,
    status,
    available: rawConnector.available === true,
    enabled: rawConnector.enabled === true,
    authMode,
    credentialScope,
    outputClassification: outputClassification || 'unclassified',
    lastValidatedAt: normalizedIso(
      rawConnector.lastValidatedAt || rawConnector.validatedAt
    ),
    errorCode,
    actions: Object.freeze([...rawActions]),
  });
}

export function normalizeConnectorOverview(
  payload = {},
  { expectedUserId = null } = {}
) {
  if (
    typeof expectedUserId !== 'string'
    || !expectedUserId
    || payload?.subjectUserId !== expectedUserId
  ) {
    return Object.freeze({
      subjectUserId: null,
      connectors: Object.freeze([]),
      errorCode: 'connector_subject_mismatch',
    });
  }
  if (!Array.isArray(payload?.connectors)) {
    return Object.freeze({
      subjectUserId: expectedUserId,
      connectors: Object.freeze([]),
      errorCode: 'invalid_connector_catalog',
    });
  }
  const connectors = payload.connectors.map(normalizeConnector);
  const connectorIds = connectors.map((connector) => connector?.id);
  if (
    connectors.some((connector) => !connector)
    || new Set(connectorIds).size !== connectorIds.length
  ) {
    return Object.freeze({
      subjectUserId: expectedUserId,
      connectors: Object.freeze([]),
      errorCode: 'invalid_connector_catalog',
    });
  }
  return Object.freeze({
    subjectUserId: expectedUserId,
    connectors: Object.freeze(connectors),
    errorCode: connectors.length > 0 ? null : 'connector_catalog_empty',
  });
}

function formatValidatedAt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function cleanOAuthParams() {
  const url = new URL(window.location.href);
  OAUTH_PARAM_NAMES.forEach((name) => url.searchParams.delete(name));
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`
  );
}

function oauthCallbackParams() {
  const params = new URLSearchParams(window.location.search);
  const connectorId = String(params.get('connector_oauth') || '').toLowerCase();
  const status = String(params.get('status') || '').toLowerCase();
  if (
    !CONNECTOR_ID_PATTERN.test(connectorId)
    || !['connected', 'error'].includes(status)
  ) {
    return null;
  }
  const rawCode = String(params.get('code') || '');
  return {
    connectorId,
    status,
    code: ERROR_CODE_PATTERN.test(rawCode) ? rawCode : '',
  };
}

function postOAuthResultToOpener(detail) {
  if (!window.opener || window.opener.closed) return false;
  window.opener.postMessage(
    { type: CONNECTOR_OAUTH_MESSAGE_TYPE, ...detail },
    window.location.origin
  );
  window.setTimeout(() => window.close(), 120);
  return true;
}

function primaryActionFor(connector) {
  if (connector.authMode !== 'oauth') return null;
  if (connector.status === 'connected') {
    return { action: 'validate', label: 'Check connection' };
  }
  if (connector.status === 'error') {
    return { action: 'reconnect', label: 'Reconnect' };
  }
  return { action: 'connect', label: 'Connect' };
}

function connectorAccessCopy(connector) {
  if (connector.authMode === 'none') {
    return {
      title: 'Public, read-only source',
      detail: `${connector.name} does not require an account or credential. Restack exposes only the reviewed read-only source queries built into the service.`,
      scope: 'No connection setup is required.',
      Icon: PublicRoundedIcon,
    };
  }
  const scope = connector.credentialScope === 'org'
    ? 'Your Organization Admin manages this shared connection.'
    : `This connection applies only to your Restack account. Each teammate authorizes their own ${connector.name} account.`;
  return {
    title: 'Your license, your connection',
    detail: `Restack does not supply or pool a ${connector.name} subscription. Authorization uses OAuth, no API key is entered here, and credentials stay server-side.`,
    scope,
    Icon: LockRoundedIcon,
  };
}

export default function ConnectorsSection() {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const userId = currentUser?.uid || null;
  const [overview, setOverview] = useState(null);
  const [overviewUserId, setOverviewUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [disconnectConnectorId, setDisconnectConnectorId] = useState(null);
  const callbackHandledRef = useRef(false);
  const oauthPopupRef = useRef(null);
  const activeUserIdRef = useRef(userId);
  const activeAuthUserRef = useRef(currentUser);
  const activeConnectorsRef = useRef([]);
  const refreshGenerationRef = useRef(0);
  const actionAbortRef = useRef(null);
  activeUserIdRef.current = userId;
  activeAuthUserRef.current = currentUser;

  useLayoutEffect(() => {
    refreshGenerationRef.current += 1;
    actionAbortRef.current?.abort();
    actionAbortRef.current = null;
    setOverview(null);
    setOverviewUserId(null);
    setError(null);
    setNotice(null);
    setBusyAction(null);
    setDisconnectConnectorId(null);
    setLoading(Boolean(userId));
    callbackHandledRef.current = false;
    const activePopup = oauthPopupRef.current?.popup;
    if (activePopup && !activePopup.closed) activePopup.close();
    oauthPopupRef.current = null;
    activeConnectorsRef.current = [];
    return () => {
      actionAbortRef.current?.abort();
      actionAbortRef.current = null;
    };
  }, [currentUser, userId]);

  const refresh = useCallback(async ({
    signal,
    silent = false,
    preserveError = false,
  } = {}) => {
    const requestUser = currentUser;
    const requestUserId = userId;
    if (!requestUser || !requestUserId) return null;
    const requestGeneration = ++refreshGenerationRef.current;
    if (!silent) setLoading(true);
    if (!preserveError) setError(null);
    try {
      const result = await getMyConnectors(requestUser, { signal });
      if (
        activeUserIdRef.current !== requestUserId
        || activeAuthUserRef.current !== requestUser
        || refreshGenerationRef.current !== requestGeneration
      ) {
        return null;
      }
      const normalized = normalizeConnectorOverview(result, {
        expectedUserId: requestUserId,
      });
      setOverview(normalized);
      setOverviewUserId(requestUserId);
      return normalized;
    } catch (requestError) {
      if (
        activeUserIdRef.current === requestUserId
        && activeAuthUserRef.current === requestUser
        && refreshGenerationRef.current === requestGeneration
        && requestError?.name !== 'AbortError'
      ) {
        setError(
          requestError?.message
          || 'Connector settings are temporarily unavailable.'
        );
      }
      return null;
    } finally {
      if (
        activeUserIdRef.current === requestUserId
        && activeAuthUserRef.current === requestUser
        && refreshGenerationRef.current === requestGeneration
      ) {
        setLoading(false);
      }
    }
  }, [currentUser, userId]);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      setOverview(null);
      setError(null);
      setNotice(null);
      setBusyAction(null);
      oauthPopupRef.current = null;
      return undefined;
    }
    const controller = new AbortController();
    void refresh({ signal: controller.signal });
    return () => controller.abort();
  }, [currentUser, refresh, userId]);

  const visibleOverview = overviewUserId === userId ? overview : null;
  const connectors = visibleOverview?.connectors || EMPTY_CONNECTORS;
  activeConnectorsRef.current = connectors;
  const connectorById = useMemo(
    () => new Map(connectors.map((connector) => [connector.id, connector])),
    [connectors]
  );
  const disconnectTarget = disconnectConnectorId
    ? connectorById.get(disconnectConnectorId) || null
    : null;
  const hasOperationInProgress = connectors.some(
    (connector) => connector.errorCode === 'operation_in_progress'
  );

  useEffect(() => {
    const handleMessage = (event) => {
      const pending = oauthPopupRef.current;
      if (event.origin !== window.location.origin) return;
      if (!pending?.popup || event.source !== pending.popup) return;
      if (event.data?.type !== CONNECTOR_OAUTH_MESSAGE_TYPE) return;
      if (event.data?.connectorId !== pending.connectorId) return;
      const connector = activeConnectorsRef.current.find(
        (item) => item.id === pending.connectorId
      );
      if (event.data?.ok) {
        setNotice(`${connector?.name || 'Connector'} is connected.`);
        setError(null);
        void refresh({ silent: true });
      } else {
        setError(
          getConnectorPublicErrorMessage(event.data?.code, connector?.name)
        );
      }
      oauthPopupRef.current = null;
      setBusyAction(null);
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [refresh]);

  useEffect(() => {
    const callback = oauthCallbackParams();
    if (
      !callback
      || callbackHandledRef.current
      || !currentUser
      || overviewUserId !== userId
    ) {
      return undefined;
    }
    callbackHandledRef.current = true;
    cleanOAuthParams();
    setBusyAction({ connectorId: callback.connectorId, action: 'validate' });
    setError(null);
    setNotice(null);
    const requestUser = currentUser;
    const requestUserId = userId;
    const controller = new AbortController();
    let cancelled = false;
    const verifyCallback = async () => {
      const refreshed = await refresh({
        signal: controller.signal,
        silent: true,
        preserveError: true,
      });
      if (
        cancelled
        || activeUserIdRef.current !== requestUserId
        || activeAuthUserRef.current !== requestUser
      ) {
        return;
      }
      const connector = refreshed?.connectors?.find(
        (item) => item.id === callback.connectorId
      );
      const ok = Boolean(
        connector?.authMode === 'oauth'
        && connector.status === 'connected'
        && connector.available === true
        && connector.enabled === true
      );
      const code = ok
        ? null
        : connector?.errorCode
          || (
            connector
              ? callback.code || 'connector_not_connected'
              : 'connector_not_supported'
          );
      const result = {
        connectorId: callback.connectorId,
        ok,
        code,
      };
      setBusyAction(null);
      if (postOAuthResultToOpener(result)) return;
      if (ok) {
        setNotice(`${connector.name} is connected.`);
        setError(null);
      } else if (refreshed) {
        setError(
          getConnectorPublicErrorMessage(code, connector?.name)
        );
      }
    };
    void verifyCallback();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [currentUser, overviewUserId, refresh, userId]);

  useEffect(() => {
    const handleFocus = () => {
      if (!currentUser) return;
      const popup = oauthPopupRef.current?.popup;
      if (popup && !popup.closed) return;
      oauthPopupRef.current = null;
      setBusyAction(null);
      void refresh({ silent: true });
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [currentUser, refresh]);

  useEffect(() => {
    if (!hasOperationInProgress || !currentUser) return undefined;
    const timer = window.setInterval(() => {
      void refresh({ silent: true, preserveError: true });
    }, 2500);
    return () => window.clearInterval(timer);
  }, [currentUser, hasOperationInProgress, refresh]);

  useEffect(() => {
    if (busyAction?.action !== 'connect') return undefined;
    const timer = window.setInterval(() => {
      const popup = oauthPopupRef.current?.popup;
      if (!popup || !popup.closed) return;
      oauthPopupRef.current = null;
      setBusyAction(null);
      void refresh({ silent: true, preserveError: true });
    }, 750);
    return () => window.clearInterval(timer);
  }, [busyAction, refresh]);

  const actionIsCurrent = useCallback((connector, action) => (
    currentUser
    && userId
    && overviewUserId === userId
    && activeUserIdRef.current === userId
    && activeAuthUserRef.current === currentUser
    && !busyAction
    && activeConnectorsRef.current.some(
      (item) => item.id === connector.id && item.actions.includes(action)
    )
  ), [busyAction, currentUser, overviewUserId, userId]);

  const startOAuth = useCallback(async (connector) => {
    if (
      connector.authMode !== 'oauth'
      || !actionIsCurrent(connector, connector.status === 'error' ? 'reconnect' : 'connect')
    ) {
      return;
    }
    const requestUser = currentUser;
    const requestUserId = userId;
    setBusyAction({ connectorId: connector.id, action: 'connect' });
    setError(null);
    setNotice(null);
    actionAbortRef.current?.abort();
    const actionController = new AbortController();
    actionAbortRef.current = actionController;
    const popup = window.open(
      '',
      `restack-${connector.id}-oauth`,
      'popup,width=620,height=760'
    );
    oauthPopupRef.current = { popup, connectorId: connector.id };
    try {
      const result = await beginConnectorOAuth(requestUser, connector.id, {
        signal: actionController.signal,
        connectorName: connector.name,
      });
      if (
        activeUserIdRef.current !== requestUserId
        || activeAuthUserRef.current !== requestUser
      ) {
        if (popup && !popup.closed) popup.close();
        return;
      }
      const authorizationUrl = validateAuthorizationUrl(
        result?.authorizationUrl,
        connector.id
      );
      if (popup && !popup.closed) popup.location.replace(authorizationUrl);
      else window.location.assign(authorizationUrl);
    } catch (connectError) {
      if (popup && !popup.closed) popup.close();
      if (
        activeUserIdRef.current !== requestUserId
        || activeAuthUserRef.current !== requestUser
      ) {
        return;
      }
      oauthPopupRef.current = null;
      setBusyAction(null);
      setError(
        connectError?.message || `${connector.name} could not be connected.`
      );
      void refresh({ silent: true, preserveError: true });
    } finally {
      if (actionAbortRef.current === actionController) {
        actionAbortRef.current = null;
      }
    }
  }, [actionIsCurrent, currentUser, refresh, userId]);

  const checkConnection = useCallback(async (connector) => {
    if (!actionIsCurrent(connector, 'validate')) return;
    const requestUser = currentUser;
    const requestUserId = userId;
    setBusyAction({ connectorId: connector.id, action: 'validate' });
    setError(null);
    setNotice(null);
    actionAbortRef.current?.abort();
    const actionController = new AbortController();
    actionAbortRef.current = actionController;
    try {
      await validateConnector(requestUser, connector.id, {
        signal: actionController.signal,
        connectorName: connector.name,
      });
      if (
        activeUserIdRef.current !== requestUserId
        || activeAuthUserRef.current !== requestUser
      ) {
        return;
      }
      setNotice(`${connector.name} connection verified.`);
      await refresh({ silent: true });
    } catch (validationError) {
      if (
        activeUserIdRef.current !== requestUserId
        || activeAuthUserRef.current !== requestUser
      ) {
        return;
      }
      await refresh({ silent: true, preserveError: true });
      if (
        activeUserIdRef.current === requestUserId
        && activeAuthUserRef.current === requestUser
      ) {
        setError(
          validationError?.message
          || `${connector.name} needs to be reconnected.`
        );
      }
    } finally {
      if (
        activeUserIdRef.current === requestUserId
        && activeAuthUserRef.current === requestUser
      ) {
        setBusyAction(null);
      }
      if (actionAbortRef.current === actionController) {
        actionAbortRef.current = null;
      }
    }
  }, [actionIsCurrent, currentUser, refresh, userId]);

  const confirmDisconnect = useCallback(async () => {
    const connector = disconnectTarget;
    if (!connector || !actionIsCurrent(connector, 'disconnect')) return;
    const requestUser = currentUser;
    const requestUserId = userId;
    setBusyAction({ connectorId: connector.id, action: 'disconnect' });
    setError(null);
    actionAbortRef.current?.abort();
    const actionController = new AbortController();
    actionAbortRef.current = actionController;
    try {
      await disconnectConnector(requestUser, connector.id, {
        signal: actionController.signal,
        connectorName: connector.name,
      });
      if (
        activeUserIdRef.current !== requestUserId
        || activeAuthUserRef.current !== requestUser
      ) {
        return;
      }
      setDisconnectConnectorId(null);
      setNotice(`${connector.name} disconnected.`);
      await refresh({ silent: true });
    } catch (disconnectError) {
      if (
        activeUserIdRef.current !== requestUserId
        || activeAuthUserRef.current !== requestUser
      ) {
        return;
      }
      setError(
        disconnectError?.message
        || `${connector.name} could not be disconnected.`
      );
      await refresh({ silent: true, preserveError: true });
    } finally {
      if (
        activeUserIdRef.current === requestUserId
        && activeAuthUserRef.current === requestUser
      ) {
        setBusyAction(null);
      }
      if (actionAbortRef.current === actionController) {
        actionAbortRef.current = null;
      }
    }
  }, [actionIsCurrent, currentUser, disconnectTarget, refresh, userId]);

  if (loading) {
    return (
      <Box aria-label="Loading connectors" sx={{ maxWidth: 920 }}>
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={220} />
          <Skeleton variant="rounded" height={180} />
        </Stack>
      </Box>
    );
  }

  const catalogUnavailable = deriveConnectorCapability({
    id: 'connectors',
    name: 'Connector catalog',
    status: 'unavailable',
    available: false,
    errorCode: visibleOverview?.errorCode || 'availability_unverified',
    actions: [],
  });

  return (
    <Stack spacing={3} sx={{ maxWidth: 920 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {connectors.length === 0 && (
        <CapabilityUnavailablePanel
          capability={catalogUnavailable}
          title="Connectors"
          actionLabel="Connect"
        />
      )}

      {connectors.map((connector) => {
        const capability = deriveConnectorCapability(connector);
        const operationInProgress =
          connector.errorCode === 'operation_in_progress';
        const operationInterrupted =
          connector.errorCode === 'operation_interrupted';
        const primary = primaryActionFor(connector);
        const connectorBusy = busyAction?.connectorId === connector.id;
        const anyBusy = Boolean(busyAction);
        const primaryAllowed = Boolean(
          primary && connector.actions.includes(primary.action)
        );
        const primaryDisabled = anyBusy
          || operationInProgress
          || capability.actionEnabled !== true
          || !primaryAllowed;
        const primaryPendingLabel = connectorBusy
          && busyAction.action === 'validate'
          ? 'Checking'
          : connectorBusy && busyAction.action === 'connect'
            ? 'Connecting'
            : primary?.label;
        const validatedAt = formatValidatedAt(connector.lastValidatedAt);
        const accessCopy = connectorAccessCopy(connector);
        const callout = operationInProgress
          ? 'Connector update in progress.'
          : operationInterrupted
            ? 'The prior connector update did not finish. You can reconnect or disconnect to recover.'
            : capability.status !== CAPABILITY_STATUSES.AVAILABLE
              ? capability.explanation
              : null;

        return (
          <Stack key={connector.id} spacing={1.5}>
            {callout && <Alert severity="info">{callout}</Alert>}
            <Paper
              variant="outlined"
              sx={{
                ...consoleCardSx(theme, { padded: false }),
                overflow: 'hidden',
              }}
            >
              <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2.5}
                  justifyContent="space-between"
                >
                  <Box sx={{ minWidth: 0, maxWidth: 620 }}>
                    <Stack
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                      sx={{ mb: 1, flexWrap: 'wrap' }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 650, letterSpacing: '-0.015em' }}
                      >
                        {connector.name}
                      </Typography>
                      <CapabilityStatusChip capability={capability} />
                      {operationInProgress && (
                        <Chip
                          size="small"
                          icon={<RefreshRoundedIcon />}
                          label="Updating"
                          sx={statusChipSx(theme, 'neutral')}
                        />
                      )}
                      {operationInterrupted && (
                        <Chip
                          size="small"
                          icon={<RefreshRoundedIcon />}
                          label="Retry available"
                          sx={statusChipSx(theme, 'neutral')}
                        />
                      )}
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', lineHeight: 1.65 }}
                    >
                      {connector.description}
                    </Typography>
                    {validatedAt && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: 'text.disabled',
                          mt: 1,
                        }}
                      >
                        Last checked {validatedAt}
                      </Typography>
                    )}
                  </Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ flexShrink: 0 }}
                  >
                    {primary && (
                      <Button
                        variant={
                          connector.status === 'connected'
                            ? 'outlined'
                            : 'contained'
                        }
                        onClick={() => {
                          if (primary.action === 'validate') {
                            void checkConnection(connector);
                          } else {
                            void startOAuth(connector);
                          }
                        }}
                        disabled={primaryDisabled}
                        startIcon={
                          connectorBusy
                          && busyAction.action !== 'disconnect'
                            ? <CircularProgress size={16} color="inherit" />
                            : <LinkRoundedIcon />
                        }
                      >
                        {primaryPendingLabel}
                      </Button>
                    )}
                    {connector.actions.includes('disconnect') && (
                      <Button
                        color="inherit"
                        onClick={() => setDisconnectConnectorId(connector.id)}
                        disabled={anyBusy}
                      >
                        Disconnect
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Box>
              <Divider />
              <Box
                sx={{
                  px: { xs: 2.5, sm: 3 },
                  py: 2.25,
                  backgroundColor: alpha(theme.palette.text.primary, 0.025),
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <accessCopy.Icon
                    sx={{ color: 'text.secondary', fontSize: 18, mt: '2px' }}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {accessCopy.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.6,
                        display: 'block',
                        mt: 0.25,
                      }}
                    >
                      {accessCopy.detail}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.6,
                        display: 'block',
                        mt: 0.75,
                      }}
                    >
                      {accessCopy.scope}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>
          </Stack>
        );
      })}

      <Alert severity="info" icon={<RefreshRoundedIcon />}>
        Connector calls are read-only and use normal Restack model credits.
        Licensed sources remain subject to the customer’s provider agreement
        and permissions.
      </Alert>

      <Dialog
        open={Boolean(disconnectTarget)}
        onClose={() => !busyAction && setDisconnectConnectorId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Disconnect {disconnectTarget?.name || 'connector'}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Restack will revoke its stored authorization. The provider account
            and subscription are not changed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => setDisconnectConnectorId(null)}
            disabled={Boolean(busyAction)}
          >
            Cancel
          </Button>
          <Button
            color="error"
            onClick={() => void confirmDisconnect()}
            disabled={Boolean(busyAction)}
          >
            {busyAction?.action === 'disconnect'
              ? 'Disconnecting'
              : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
